import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import CaseProgressIndicator from '../../components/navigation/CaseProgressIndicator';
import QuickActionMenu from '../../components/navigation/QuickActionMenu';
import PersonaCard from './components/PersonaCard';
import MetadataForm from './components/MetadataForm';
import FileUploadZone from './components/FileUploadZone';
import ConfirmationSummary from './components/ConfirmationSummary';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

import { useAuth } from '../../contexts/AuthContext';
import { checkAvailableCredits, createCase, uploadCaseFile, finalizeCase, consumeCredit } from '../../services/caseService';
import { runPhase1Triage, runPhase3ReportGeneration } from '../../services/openai/orchestrationService';
import { getCreditBreakdown } from '../../services/creditService';
import { validateFileUpload } from '../../services/securityService';
import { hasCardOnFile } from '../../services/stripeService';
import InsufficientCreditsModal from '../../components/InsufficientCreditsModal';
import CaseSubmissionWarningModal from '../../components/CaseSubmissionWarningModal';
import AIProcessingOverlay from '../../components/AIProcessingOverlay';
import toast, { Toaster } from 'react-hot-toast';

const NewCaseCreationWizard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessAllowed, setAccessAllowed] = useState(false);
  const [accessBlockedReason, setAccessBlockedReason] = useState(null); // 'no-credits' | 'no-credits-no-card'
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [formData, setFormData] = useState({
    incidentDate: '',
    incidentTime: '',
    propertyAddress: '',
    propertyType: '',
    allegationType: '',
    claimNumber: '',
    caseNumber: '',
    criminalCaseNumber: '',
    estimatedLoss: '',
    additionalContext: ''
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [showSubmissionWarningModal, setShowSubmissionWarningModal] = useState(false);
  const [creditBreakdown, setCreditBreakdown] = useState(null);
  const [errors, setErrors] = useState({});

  // ── Gate: check credits first, then payment method only if no credits ────
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        // 1. Credits check (has priority — free trial credits count here)
        const creditResult = await checkAvailableCredits(user.id);
        if (creditResult?.success && creditResult.credits <= 0) {
          // 2. Only check payment method when there are no credits
          const cardResult = await hasCardOnFile(user.id);
          const hasCard = cardResult?.success && cardResult.hasCard;
          setAccessBlockedReason(hasCard ? 'no-credits' : 'no-credits-no-card');
          setAccessAllowed(false);
          setAccessChecked(true);
          return;
        }
        // Credits available — allow access
        setAccessAllowed(true);
      } catch {
        // On error let them through; wizard's own submission check will catch it
        setAccessAllowed(true);
      } finally {
        setAccessChecked(true);
      }
    })();
  }, [user?.id]);

  // Spinner while the async check runs
  if (!accessChecked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-foreground-secondary">Checking account status...</p>
        </div>
      </div>
    );
  }

  // Blocked — show explanatory modal overlay instead of the wizard
  if (!accessAllowed) {
    const isNoCard = accessBlockedReason === 'no-credits-no-card';
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-elevation-lg p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-warning/10 mb-5">
            <Icon name={isNoCard ? 'CreditCard' : 'Coins'} size={32} className="text-warning" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">
            {isNoCard ? 'No Payment Method on File' : 'No Credits Remaining'}
          </h2>
          <p className="text-foreground-secondary mb-6">
            {isNoCard
              ? 'You have no credits and no payment method on file. Add a payment method so you can purchase credits and create your first case.'
              : 'You have no credits remaining. Purchase credits to create a new case.'}
          </p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() =>
                navigate(
                  isNoCard
                    ? '/client-dashboard/financial?tab=payment-method'
                    : '/client-dashboard/financial?action=buy-credits'
                )
              }
              variant="default"
              className="w-full"
            >
              <Icon name={isNoCard ? 'CreditCard' : 'ShoppingCart'} size={16} className="mr-2" />
              {isNoCard ? 'Add Payment Method' : 'Buy Credits'}
            </Button>
            <Button
              onClick={() => navigate('/client-dashboard')}
              variant="outline"
              className="w-full"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const personas = [
    {
      id: 'insurance-adjuster',
      title: 'Insurance Adjuster',
      icon: 'Shield',
      description: 'Making coverage decisions and subrogation assessments for fire loss claims',
    },
    {
      id: 'plaintiff-attorney',
      title: 'Plaintiff Attorney',
      icon: 'Scale',
      description: 'Evaluating case viability and developing litigation strategy for fire damage claims',
    },
    {
      id: 'defense-attorney',
      title: 'Defense Attorney',
      icon: 'Briefcase',
      description: 'Assessing exposure and settlement posture in fire-related litigation',
    },
    {
      id: 'siu-investigator',
      title: 'SIU Investigator',
      icon: 'Search',
      description: 'Focused on fraud and arson detection in suspicious fire claims',
    },
    {
      id: 'subrogation-specialist',
      title: 'Subrogation Specialist',
      icon: 'RefreshCw',
      description: 'Evaluating recovery potential and third-party liability in fire loss claims',
    },
    {
      id: 'prosecutor',
      title: 'Prosecutor',
      icon: 'Gavel',
      description: 'Evaluating malice indicators and criminal elements in fire cases',
    },
    {
      id: 'fire-marshal',
      title: 'Fire Marshal/AHJ',
      icon: 'Flame',
      description: 'Reviewing code compliance observations and fire safety violations',
    },
    {
      id: 'risk-manager',
      title: 'Risk Manager',
      icon: 'TrendingUp',
      description: 'Developing prevention strategies and risk mitigation protocols',
    },
    {
      id: 'property-owner',
      title: 'Property Owner/Insured',
      icon: 'Home',
      description: 'Understanding claim processes and documentation requirements',
    },
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!selectedPersona) {
        newErrors.persona = 'Please select your role to continue';
      }
    }

    if (step === 2) {
      if (!formData?.incidentDate) {
        newErrors.incidentDate = 'Incident date is required';
      }
      if (!formData?.propertyAddress) {
        newErrors.propertyAddress = 'Property address is required';
      }
      if (!formData?.propertyType) {
        newErrors.propertyType = 'Property type is required';
      }
      if (!formData?.allegationType) {
        newErrors.allegationType = 'Allegation type is required';
      }
    }

    if (step === 3) {
      if (files?.length === 0) {
        newErrors.files = 'Please upload at least one image or document';
      }
      const imageFiles = files?.filter((f) => f?.type?.startsWith('image/'));
      if (imageFiles?.length === 0) {
        newErrors.files = 'At least one image file is required for analysis';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFileDrop = async (acceptedFiles) => {
    // Validate each file
    const validationPromises = acceptedFiles?.map(file => validateFileUpload(file));
    const validationResults = await Promise.all(validationPromises);

    const invalidFiles = validationResults?.filter(result => !result?.valid);
    if (invalidFiles?.length > 0) {
      toast?.error(invalidFiles?.[0]?.error);
      return;
    }

    setFiles(prev => [...prev, ...acceptedFiles]);
    toast?.success(`${acceptedFiles?.length} file(s) added`);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    if (!user) {
      toast?.error('You must be logged in to submit a case');
      return;
    }

    // Check credits first
    const creditsResult = await checkAvailableCredits(user?.id);
    if (!creditsResult?.success || creditsResult?.credits <= 0) {
      // Get credit breakdown for modal
      const breakdownResult = await getCreditBreakdown(user?.id);
      if (breakdownResult?.success) {
        setCreditBreakdown(breakdownResult?.breakdown);
      }
      setShowInsufficientCreditsModal(true);
      return;
    }

    // Show submission warning modal
    setShowSubmissionWarningModal(true);
  };

  const handleConfirmSubmission = async () => {
    try {
      setIsSubmitting(true);
      setShowSubmissionWarningModal(false);

      toast?.success('Creating case...');

      const caseResult = await createCase(user?.id, {
        persona: selectedPersona,
        incidentDate: formData?.incidentDate,
        incidentTime: formData?.incidentTime,
        propertyAddress: formData?.propertyAddress,
        propertyType: formData?.propertyType,
        allegationType: formData?.allegationType,
        claimNumber: formData?.claimNumber,
        caseNumber: formData?.caseNumber,
        criminalCaseNumber: formData?.criminalCaseNumber,
        estimatedLoss: formData?.estimatedLoss,
        additionalContext: formData?.additionalContext
      });

      if (!caseResult?.success) {
        throw new Error(caseResult?.error || 'Failed to create case');
      }

      const caseId = caseResult?.data?.id;

      // Finalize case to prevent edits
      await finalizeCase(caseId);

      toast?.success('Uploading files...');

      const uploadPromises = files?.map(file => uploadCaseFile(user?.id, caseId, file));
      const uploadResults = await Promise.all(uploadPromises);

      const failedUploads = uploadResults?.filter(r => !r?.success);
      if (failedUploads?.length > 0) {
        console.error('Some uploads failed:', failedUploads);
      }

      toast?.success('Running AI triage analysis...');

      const triageResult = await runPhase1Triage(
        caseId,
        {
          persona: selectedPersona,
          incidentDate: formData?.incidentDate,
          propertyAddress: formData?.propertyAddress,
          propertyType: formData?.propertyType,
          allegationType: formData?.allegationType,
          additionalContext: formData?.additionalContext
        },
        uploadResults?.filter(r => r?.success)?.map(r => r?.data)
      );

      if (!triageResult?.success) {
        console.error('Triage failure detail:', triageResult?.error);
        toast?.error(`Triage failed: ${triageResult?.error || 'Unknown error'}`);
        navigate('/client-dashboard');
        return;
      }

      if (triageResult?.needsClarification) {
        toast?.success('Triage complete. Please answer clarifying questions.');
        navigate(`/clarifying-questions/${caseId}`);
      } else {
        // No clarification needed — generate report directly
        toast?.loading('Generating report — this may take a minute...', { id: 'report-gen', duration: 120000 });
        const reportResult = await runPhase3ReportGeneration(caseId, user?.id);
        toast?.dismiss('report-gen');

        if (!reportResult?.success) {
          toast?.error(reportResult?.error || 'Failed to generate report');
          navigate('/client-dashboard');
          return;
        }

        const creditResult = await consumeCredit(user?.id, caseId);
        if (!creditResult?.success) console.error('Credit consumption failed:', creditResult?.error);

        toast?.success('Report generated successfully!');
        navigate(`/report-viewer/${caseId}`);
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast?.error(error?.message || 'Failed to submit case');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" />
      <Sidebar userRole="client" />
      <QuickActionMenu userRole="client" />
      <div className="lg:ml-60 min-h-screen">
        <div className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold">New Case Creation</h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Complete all steps to submit your fire forensics case
                </p>
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate('/client-dashboard')}
                iconName="X"
              >
                Cancel
              </Button>
            </div>
            <CaseProgressIndicator currentStep={currentStep} totalSteps={4} />
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="bg-card rounded-lg border border-border p-6 md:p-8 lg:p-10">
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold mb-2">
                    Select Your Role
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Choose the role that best describes your position. This helps us tailor the analysis to your specific needs.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {personas?.map((persona) => (
                    <PersonaCard
                      key={persona?.id}
                      persona={persona}
                      isSelected={selectedPersona === persona?.id}
                      onSelect={() => setSelectedPersona(persona?.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold mb-2">
                    Case Details
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Provide essential information about the fire incident. All required fields must be completed.
                  </p>
                </div>

                <MetadataForm
                  formData={formData}
                  onChange={setFormData}
                  selectedPersona={selectedPersona}
                  errors={errors}
                />
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold mb-2">
                    Upload Evidence
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Upload photos of the fire scene, damage, and potential ignition sources. You can also include relevant documents as PDFs.
                  </p>
                </div>

                <FileUploadZone
                  files={files}
                  onFilesChange={setFiles}
                  caseId={null}
                  userId={user?.id}
                  onFileDrop={handleFileDrop}
                  errors={errors}
                />
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold mb-2">
                    Review & Submit
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Review your case details carefully. This is a one-time submission.
                  </p>
                </div>

                <ConfirmationSummary
                  persona={selectedPersona}
                  formData={formData}
                  files={files}
                />
              </div>
            )}

            <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1 || isSubmitting}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Back
              </Button>

              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  Step {currentStep} of 4
                </span>
                {currentStep < 4 ? (
                  <Button
                    onClick={handleNext}
                    disabled={isSubmitting}
                    iconName="ChevronRight"
                    iconPosition="right"
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    iconName="Send"
                    iconPosition="right"
                  >
                    Submit Case
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Processing Overlay */}
      <AIProcessingOverlay isVisible={isSubmitting} />

      {/* Modals */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        creditBreakdown={creditBreakdown}
      />

      <CaseSubmissionWarningModal
        isOpen={showSubmissionWarningModal}
        onClose={() => setShowSubmissionWarningModal(false)}
        onConfirm={handleConfirmSubmission}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default NewCaseCreationWizard;